/**
 * Jungle temple (vanilla JungleTemplePiece): a 12 x 15 mossy-cobblestone
 * temple of three storeys — the entrance hall, an upper room under a stepped
 * cap, and a cellar reached by a stair, with chests in the cellar. Vanilla's
 * levers, dispensers and tripwire have no counterpart here; the chiseled stone
 * bricks mark where the lever puzzle sits.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR, TORCH_ID } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import { block, type Builder, cell, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType } from './kit';

const STONE = cell.mix([[block('Cobblestone'), 3], [block('Mossy Cobblestone'), 2]]);
const CHISELED = block('Chiseled Stone Bricks');

function buildTemple(b: Builder): void {
  const y = b.y;
  b.fill(-1, y + 1, -1, 12, y + 14, 15, AIR);
  b.foundations(0, 0, 11, 14, y, STONE, 8);
  b.room(0, y - 4, 0, 11, y + 5, 14, STONE);   // shell: cellar and main floor
  b.fill(1, y, 1, 10, y, 13, STONE);           // floor between them
  b.room(2, y + 5, 2, 9, y + 9, 12, STONE);    // upper room
  b.room(4, y + 9, 4, 7, y + 11, 10, STONE);   // roof cap
  b.fill(5, y + 12, 6, 6, y + 12, 8, STONE);
  // entrance with a chiseled lintel, and windows down the sides
  b.fill(5, y + 1, 0, 6, y + 3, 0, AIR);
  b.fill(4, y + 4, 0, 7, y + 4, 0, CHISELED);
  for (let v = 3; v <= 11; v += 4) { b.set(0, y + 2, v, AIR); b.set(11, y + 2, v, AIR); }
  // up to the top room, and a stair down to the cellar
  b.fill(5, y + 5, 6, 6, y + 5, 8, AIR);
  b.fill(2, y, 5, 3, y, 9, AIR);
  for (let k = 0; k < 4; k++) b.fill(2, y - 4 + k, 5 + k, 3, y - 4 + k, 5 + k, STONE);
  // the cellar: the lever wall and two chests
  for (let u = 7; u <= 9; u++) b.set(u, y - 2, 1, CHISELED);
  b.chest(9, y - 3, 12, F.NV);
  b.chest(2, y - 3, 12, F.NV);
  b.set(5, y + 6, 11, TORCH_ID);
}

function jungleTemple(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if ((c.biome !== Biome.Jungle && c.biome !== Biome.BambooJungle) || c.height < SEA_LEVEL + 1) return null;
  return [centredPiece(x, z, int(rnd, 4), 12, 15, c.height, true, seedOf(rnd), buildTemple)];
}

export const JUNGLE_TEMPLE: StructureType = {
  name: 'Jungle Temple', spacing: 32, separation: 8, salt: 14357619, reach: 1, generate: jungleTemple,
};
